export type LicenseType =
  | null
  | "basic"
  | "premium"
  | "basic non-media"
  | "premium non-media"
  | "intelligence starter"
  | "intelligence basic"
  | "intelligence premium";

const INTERNAL_GP_NUMBERS = [
  "6_009359",
  "6_021987",
  "6_015071",
  "6_013232",
  "6_016833",
  "6_000651",
  "6_011823",
  "6_007264",
  "6_010043",
  "6_017545",
] as const;

// Derive the union type from the array
export type GpNumber = (typeof INTERNAL_GP_NUMBERS)[number];

export type Supplier = (typeof VALID_SUPPLIERS)[number];

export type Language = "de" | "en" | "es" | null;

const VALID_SUPPLIERS = ["XINHUA", "AP", "newsaktuell", "dpa"] as const;

/**
 * Sets the license type as a custom dimension in Matomo (dimension ID 1, scope: visit).
 * Call this once per visit/session, as soon as the license type is known.
 */
export function useCustomDimensions() {
  const setLicenseType = (licenseType: LicenseType) => {
    const validTypes: LicenseType[] = [
      "basic",
      "premium",
      "basic non-media",
      "premium non-media",
      "intelligence starter",
      "intelligence basic",
      "intelligence premium",
    ];
    if (!validTypes.includes(licenseType)) {
      return;
    }
    (window as any)._paq = (window as any)._paq || [];
    (window as any)._paq.push(["setCustomDimension", 1, licenseType]);
  };

  // GpNumber type guard
  function isInternalGpNumber(value: string | undefined): value is GpNumber {
    return (
      value !== undefined &&
      (INTERNAL_GP_NUMBERS as readonly string[]).includes(value)
    );
  }

  function isExternalGpNumber(value: string | undefined): value is string {
    return (
      value !== undefined &&
      !(INTERNAL_GP_NUMBERS as readonly string[]).includes(value)
    );
  }

  /**
   * Sends intern or extern state plus GPNumber to Matomo,
   * depending if the user is GPNumber extern or intern
   **/
  const setInternalGpNumber = (gpNumber: GpNumber) => {
    if (!INTERNAL_GP_NUMBERS.includes(gpNumber)) {
      return;
    }
    (window as any)._paq = (window as any)._paq || [];
    (window as any)._paq.push(["setCustomDimension", 8, gpNumber]);
  };

  // Accept any string for external gpNumbers
  const setExternalGpNumber = (gpNumber: string) => {
    if (!INTERNAL_GP_NUMBERS.includes(gpNumber as GpNumber)) {
      (window as any)._paq = (window as any)._paq || [];
      (window as any)._paq.push(["setCustomDimension", 9, gpNumber]);
    }
  };

  /**
   * Sends user type e.g. intern or extern to Matomo depending if the user is GPNumber extern or intern,
   * which will be identified by the corresponding type guards above
   **/
  const setUserType = (userType: string) => {
    (window as any)._paq = (window as any)._paq || [];
    (window as any)._paq.push(["setCustomDimension", 7, userType]);
  };

  /**
   * Type guard to check if a value is a valid language (de, en, es).
   * Matomo does NOT accept null.
   */
  function isValidLanguage(value: unknown): value is Exclude<Language, null> {
    return value === "de" || value === "en" || value === "es";
  }

  /**
   * Sets the language as a custom dimension in Matomo (dimension ID 4).
   * Only sets if the language is valid and not null.
   * If language is null, skips setting (Matomo will retain previous value).
   */
  const setLanguage = (language: Language) => {
    if (!isValidLanguage(language)) {
      return;
    }
    (window as any)._paq = (window as any)._paq || [];
    (window as any)._paq.push(["setCustomDimension", 4, language]);
  };

  function isSupplier(value: unknown): value is Supplier {
    return (
      typeof value === "string" &&
      (VALID_SUPPLIERS as readonly string[]).includes(value)
    );
  }

  const setSupplier = (supplier: Supplier | undefined) => {
    if (!isSupplier(supplier)) {
      return;
    }
    (window as any)._paq = (window as any)._paq || [];
    (window as any)._paq.push(["setCustomDimension", 5, supplier]);
  };

  const setSector = (sector: string | null | undefined) => {
    (window as any)._paq = (window as any)._paq || [];
    if (!sector || !sector.trim()) {
      //handle null/undefined/empty/whitespace by clearing the dimension
      (window as any)._paq.push(["deleteCustomDimension", 6]);
      return;
    }
    (window as any)._paq.push(["setCustomDimension", 6, sector.trim()]);
  };

  return {
    setLicenseType,
    setUserType,
    setLanguage,
    setInternalGpNumber,
    setExternalGpNumber,
    isInternalGpNumber,
    isExternalGpNumber,
    isValidLanguage,
    setSupplier,
    isSupplier,
    setSector,
  };
}
